/**
 * 새 배포 감지 → 안전한 순간에 한 번 새로고침 (2026-09-25)
 *
 * 왜 필요한가
 * -----------
 * 이 앱은 한 번 열면 메뉴를 옮겨도 페이지를 다시 받지 않는다(한 화면 앱).
 * 그래서 창을 며칠씩 열어 두는 매장 PC·휴대폰은 **배포가 나가도 옛 화면을 계속 쓴다.**
 * 2026-09-25 우장산: 9/10 이후 넣은 사유서 숫자 배지가 원장 PC·대표 폰에서 안 보였다.
 * 서버·운영 화면은 정상이었고, 두 기기 모두 9/10 이전 화면을 쓰고 있었다
 * (탭을 바꿔도 숫자 요청이 한 번도 안 나감 — 새 화면은 탭마다 보낸다).
 * 고친 것이 원장 화면에 닿지 않으면 고친 게 아니다.
 *
 * 동작
 * ----
 * - 5분마다(그리고 다른 앱에서 돌아올 때) index.html 을 새로 받아 main.<해시>.js 를 비교한다.
 * - 다르면 '새 배포 있음'만 표시해 두고, **메뉴를 옮기는 순간** 새로고침한다.
 *   입력 중인 화면을 갑자기 날리면 안 되므로 그 자리에서 바로 새로고침하지 않는다.
 * - 예외: 10분 넘게 다른 앱에 가 있다 돌아온 경우는 돌아온 순간 새로고침한다
 *   (그 사이 입력하던 건 없다고 본다). 입력칸에 포커스가 있으면 그때도 미룬다.
 * - 같은 새 해시로는 한 번만 새로고침한다 — 중간 캐시 때문에 새로고침 뒤에도 옛 화면이면
 *   무한 새로고침에 빠지므로.
 * - 개발 서버(해시 없음)에서는 아무것도 하지 않는다.
 */
import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

const CHECK_INTERVAL_MS = 5 * 60 * 1000;
const LONG_AWAY_MS = 10 * 60 * 1000;
const MAIN_RE = /\/static\/js\/(main\.[0-9a-f]+\.js)/;
const RELOADED_KEY = 'newbuild_reloaded_for';

function currentMain(): string | null {
  const scripts = Array.from(document.querySelectorAll('script[src]')) as HTMLScriptElement[];
  for (const s of scripts) {
    const m = s.src.match(MAIN_RE);
    if (m) return m[1];
  }
  return null;
}

async function latestMain(): Promise<string | null> {
  try {
    const res = await fetch(`/index.html?_v=${Date.now()}`, { cache: 'no-store' });
    if (!res.ok) return null;
    const m = (await res.text()).match(MAIN_RE);
    return m ? m[1] : null;
  } catch {
    return null; // 인터넷이 잠깐 끊긴 것 — 다음에 다시 본다
  }
}

function isTyping(): boolean {
  const el = document.activeElement as HTMLElement | null;
  if (!el) return false;
  const tag = el.tagName;
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || el.isContentEditable;
}

export default function NewBuildWatcher() {
  const location = useLocation();
  const pendingRef = useRef<string | null>(null);
  const hiddenAtRef = useRef<number | null>(null);
  const firstPathRef = useRef(true);

  const reloadFor = (next: string) => {
    if (sessionStorage.getItem(RELOADED_KEY) === next) return; // 이미 한 번 시도함
    sessionStorage.setItem(RELOADED_KEY, next);
    window.location.reload();
  };

  useEffect(() => {
    const mine = currentMain();
    if (!mine) return undefined; // 개발 서버

    const check = async () => {
      const next = await latestMain();
      if (next && next !== mine) pendingRef.current = next;
    };

    const onVisibility = async () => {
      if (document.visibilityState === 'hidden') {
        hiddenAtRef.current = Date.now();
        return;
      }
      const awayMs = hiddenAtRef.current ? Date.now() - hiddenAtRef.current : 0;
      hiddenAtRef.current = null;
      await check();
      if (pendingRef.current && awayMs >= LONG_AWAY_MS && !isTyping()) {
        reloadFor(pendingRef.current);
      }
    };

    check();
    const timer = window.setInterval(check, CHECK_INTERVAL_MS);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, []);

  // 메뉴를 옮기는 순간 = 입력 중인 게 없는 순간
  useEffect(() => {
    if (firstPathRef.current) {
      firstPathRef.current = false;
      return;
    }
    if (pendingRef.current) reloadFor(pendingRef.current);
  }, [location.pathname]);

  return null;
}
